import type { Problem } from "@baton/api";
import { useI18n } from "@baton/i18n";
import { Copy, RefreshCw } from "@baton/icons";
import { useState } from "react";
import { cn } from "../../cn.ts";
import { Button } from "../../primitives/buttons/Button/Button.tsx";

/**
 * FR-015-090, § Error: inline, panel and full-page forms, always rendering the problem
 * `title`, the `detail`, the `request_id` with a copy control and Retry, and never a stack
 * trace or a raw body.
 *
 * Custom in `packages/ui`: the registry's error-state is a chat assistant block and shadcn/ui has
 * none, so it is drawn on the utilities of the token theme with the slice (a) Button (2026-09-18,
 * slice (f)). The panel is the red tint under a red border at the medium radius, the inline form is
 * the same at the small step for a picker or a row, and the full page form drops the frame and
 * centres its words.
 */
export interface ErrorStateProps {
  readonly problem: Problem;
  readonly onRetry?: () => void;
  readonly form?: "inline" | "panel" | "full-page";
  readonly className?: string;
}

/** A failure's title where a host states one in a line of its own (a group that would not load more). */
export const errorTitleClassName = "m-0 font-(weight:--text-body-strong-weight)";

export function RequestId({ id }: { readonly id: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <>
      {/* Read after the title in the same block, on the red tint, where the tertiary falls under 4.5:1 in light: the secondary ink (§ Ink levels, 2026-10-15). */}
      <span data-slot="request-id" className="font-mono text-caption text-text-secondary">
        {t("ui.requestId", { id })}
      </span>
      <Button
        size="sm"
        variant="ghost"
        iconStart={<Copy />}
        onClick={() => {
          void navigator.clipboard.writeText(id).then(() => {
            setCopied(true);
          });
        }}
      >
        {copied ? t("ui.copied") : t("ui.copyRequestId")}
      </Button>
    </>
  );
}

const FORM: Readonly<Record<NonNullable<ErrorStateProps["form"]>, string>> = {
  inline: "rounded-lg border border-accent-red-solid bg-accent-red-bg p-2 text-small",
  panel: "rounded-lg border border-accent-red-solid bg-accent-red-bg p-3",
  "full-page": "items-center px-3 py-16 text-center",
};

export function ErrorState({ problem, onRetry, form = "panel", className }: ErrorStateProps) {
  const { t } = useI18n();
  return (
    <div data-slot="error-state" data-form={form} role="alert" className={cn("flex flex-col gap-1 text-text-primary", FORM[form], className)}>
      <p className={errorTitleClassName}>{problem.title}</p>
      {/* Read after the title in the same block: the secondary ink (§ Ink levels), which holds 6.29:1 on the light red tint where the tertiary fell to 4.45:1 (2026-10-23, the gallery run). */}
      {problem.detail !== undefined && <p className="m-0 text-text-secondary">{problem.detail}</p>}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {problem.request_id.length > 0 && <RequestId id={problem.request_id} />}
        {onRetry !== undefined && (
          <Button size="sm" variant="secondary" iconStart={<RefreshCw />} onClick={onRetry}>
            {t("ui.retry")}
          </Button>
        )}
      </div>
    </div>
  );
}
